<script setup lang="ts">
// Tag editor: the selected tags as removable chips, every tag already known in
// the fleet as one-click suggestions, and a text field for new ones
// (Enter, comma or space commits). Values are normalised like the server does
// (trim, lowercase, dedupe) so what you see is what gets stored.
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { parseTags, tagKey } from "@/composables/useAgentFilter";

const props = defineProps<{
  modelValue: string[];
  /** Tags already in use anywhere (agents + enroll tokens) — offered as chips. */
  suggestions: string[];
  disabled?: boolean;
  /** data-test for the free-text input (tests type new tags here). */
  inputTestId?: string;
}>();
const emit = defineEmits<{ (e: "update:modelValue", v: string[]): void }>();

const { t } = useI18n();
const draft = ref("");

const selected = computed(() => props.modelValue);
const available = computed(() =>
  [...new Set(props.suggestions.map((s) => s.trim().toLowerCase()).filter(Boolean))]
    .filter((s) => !selected.value.includes(s))
    .sort((a, b) => tagKey(a).localeCompare(tagKey(b)) || a.localeCompare(b)),
);

function set(next: string[]) {
  emit("update:modelValue", [...new Set(next)]);
}
function add(tag: string) {
  const clean = parseTags(tag);
  if (clean.length) set([...selected.value, ...clean]);
}
function remove(tag: string) {
  set(selected.value.filter((x) => x !== tag));
}
function commitDraft() {
  if (draft.value.trim()) add(draft.value);
  draft.value = "";
}
function onKey(e: KeyboardEvent) {
  if (e.key === "Enter" || e.key === "," || e.key === " ") {
    e.preventDefault();
    commitDraft();
  } else if (e.key === "Backspace" && !draft.value && selected.value.length) {
    remove(selected.value[selected.value.length - 1]);
  }
}
</script>

<template>
  <div class="tag-editor" :data-disabled="disabled ? 'true' : undefined">
    <div class="tag-editor__selected tags" data-test="tag-editor-selected">
      <span v-if="!selected.length" class="tag-editor__empty">{{ t("tags.none") }}</span>
      <button
        v-for="tag in selected"
        :key="tag"
        type="button"
        class="tag"
        data-active="true"
        :data-test="`tag-selected-${tag}`"
        :disabled="disabled"
        :title="t('tags.remove')"
        @click="remove(tag)"
      >
        {{ tag }} ×
      </button>
      <input
        v-model="draft"
        type="text"
        class="tag-editor__input"
        :placeholder="t('tags.add_placeholder')"
        :disabled="disabled"
        :data-test="inputTestId ?? 'tag-editor-input'"
        @keydown="onKey"
        @blur="commitDraft"
      />
    </div>
    <div v-if="available.length" class="tag-editor__known">
      <span class="tag-editor__label">{{ t("tags.known") }}</span>
      <div class="tags">
        <button
          v-for="tag in available"
          :key="tag"
          type="button"
          class="tag"
          :data-test="`tag-suggest-${tag}`"
          :disabled="disabled"
          @click="add(tag)"
        >
          + {{ tag }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tag-editor {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.tag-editor__selected {
  align-items: center;
  padding: 0.35rem 0.45rem;
  border: 1px solid var(--color-border);
  border-radius: 0.4rem;
  background: var(--color-bg);
  min-height: 2.2rem;
}
.tag-editor__empty {
  font-size: 0.8rem;
  color: var(--color-fg-dim);
}
.tag-editor__input {
  flex: 1 1 8rem;
  min-width: 8rem;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.85rem;
  padding: 0.15rem 0.25rem;
  outline: none;
}
.tag-editor__known {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.tag-editor__label {
  font-size: 0.75rem;
  color: var(--color-fg-dim);
}
.tag-editor[data-disabled] {
  opacity: 0.6;
}
</style>
